import { apiFetch, type ApiResponse, tokenStorage } from "./api";
import { getWebSocketBase } from "../utils/wsBase";
import type { OkResponse, ProfileResponse } from "./types";

export interface LikeResponse {
  liked: boolean;
  matched: boolean;
  matchId: string | null;
}

export interface MatchItem {
  id: string;
  createdAt: string;
  peer: ProfileResponse;
}

export async function sendLike(userId: string): Promise<ApiResponse<LikeResponse>> {
  return apiFetch<LikeResponse>("/api/likes", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });
}

export interface SuperLikeResponse {
  ok: boolean;
  /** Купленный баланс (не сгорает). */
  superLikesBalance?: number;
  /** Остаток квоты окна подписки; null — нет Premium. */
  premiumSuperlikesLeft?: number | null;
  /** Дата YYYY-MM-DD, когда остаток окна сгорает; null — нет Premium. */
  premiumSuperlikesPeriodEnd?: string | null;
  matched?: boolean;
  matchId?: string | null;
}

export async function sendSuperLike(
  userId: string,
  opts?: { message?: string }
): Promise<ApiResponse<SuperLikeResponse>> {
  const body: { userId: string; message?: string } = { userId };
  const m = (opts?.message ?? "").trim();
  if (m) body.message = m;
  return apiFetch<SuperLikeResponse>("/api/superlikes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface UnlikeResponse {
  ok: boolean;
  removed: boolean;
  wasSuper?: boolean;
  superLikesBalance?: number;
}

/** Отмена свайпа: удаляет лайк на сервере. Отменённая супер симпатия НЕ возвращается
 * (#1254): `superLikesBalance` в ответе — текущий остаток, без возврата. */
export async function undoLike(userId: string): Promise<ApiResponse<UnlikeResponse>> {
  return apiFetch<UnlikeResponse>(`/api/likes/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

/** Пропуск анкеты (свайп влево): сервер запоминает и больше не показывает её в ленте. */
export async function passUser(userId: string): Promise<ApiResponse<{ ok: boolean; passed: boolean }>> {
  return apiFetch<{ ok: boolean; passed: boolean }>("/api/passes", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });
}

/** «Вернуть» пропущенную анкету в ленту. */
export async function undoPass(userId: string): Promise<ApiResponse<{ ok: boolean; removed: boolean }>> {
  return apiFetch<{ ok: boolean; removed: boolean }>(`/api/passes/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

/** Списывает один возврат к предыдущей карточке. 402 — лимит исчерпан. */
export async function consumeUndoQuota(): Promise<ApiResponse<{ ok: boolean; undosLeft: number | null }>> {
  return apiFetch<{ ok: boolean; undosLeft: number | null }>("/api/undos/consume", { method: "POST" });
}

export async function getMatches(): Promise<ApiResponse<MatchItem[]>> {
  return apiFetch<MatchItem[]>("/api/matches");
}

export interface LikeListEntry {
  profile: ProfileResponse;
  isSuperLike: boolean;
  superMessage?: string | null;
  createdAt: string;
  matched: boolean;
}

export async function getLikesReceived(): Promise<ApiResponse<LikeListEntry[]>> {
  return apiFetch<LikeListEntry[]>("/api/likes/received");
}

/** Счётчики профиля: суперлайки считаются независимо от мэтчей (#1301). */
export async function getLikesStats(): Promise<ApiResponse<{superLikesReceived: number}>> {
  return apiFetch<{superLikesReceived: number}>("/api/likes/stats");
}

export async function getLikesSent(): Promise<ApiResponse<LikeListEntry[]>> {
  return apiFetch<LikeListEntry[]>("/api/likes/sent");
}

export function createChatWebSocket(conversationId: string): WebSocket | null {
  const token = tokenStorage.getAccessToken();
  if (!token) return null;
  const base = getWebSocketBase();
  if (!base) return null;
  return new WebSocket(
    `${base}/api/ws/chats/${encodeURIComponent(conversationId)}?token=${encodeURIComponent(token)}`,
  );
}

export function createUpdatesWebSocket(): WebSocket | null {
  const token = tokenStorage.getAccessToken();
  if (!token) return null;
  const base = getWebSocketBase();
  if (!base) return null;
  return new WebSocket(`${base}/api/ws/updates?token=${encodeURIComponent(token)}`);
}

export interface QrScanApiItem {
  id: string;
  scannedUserId: string;
  scannerUserId: string;
  scannerProfile: ProfileResponse;
  compatibility: number;
  viewed: boolean;
  timestamp: string;
}

export async function recordQrScan(
  scannedUserId: string,
  compatibility: number,
): Promise<ApiResponse<QrScanApiItem>> {
  return apiFetch<QrScanApiItem>("/api/qr-scans", {
    method: "POST",
    body: JSON.stringify({ scannedUserId, compatibility }),
  });
}

export async function fetchUnviewedQrScans(): Promise<ApiResponse<QrScanApiItem[]>> {
  return apiFetch<QrScanApiItem[]>("/api/qr-scans/unviewed");
}

/** История сканов (#779): все сканы меня, включая просмотренные. */
export async function fetchQrScanHistory(): Promise<ApiResponse<QrScanApiItem[]>> {
  return apiFetch<QrScanApiItem[]>("/api/qr-scans/history");
}

export async function markQrScanViewed(scanId: string): Promise<ApiResponse<OkResponse>> {
  return apiFetch<OkResponse>(`/api/qr-scans/${encodeURIComponent(scanId)}/viewed`, {
    method: "POST",
  });
}

/** Персональная ссылка-приглашение (issue #273): переиспользуем существующий роут
 *  POST /api/invites — он создаёт (или возвращает) InviteCode владельца. */
export interface InviteInfo {
  code: string;
  url: string;
  inviterUserId: string;
}

export async function createInvite(): Promise<ApiResponse<InviteInfo>> {
  return apiFetch<InviteInfo>("/api/invites", {
    method: "POST",
    body: JSON.stringify({}),
  });
}
